import React, {useCallback, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import {DataLoadingStatus, DfEntryStatus} from "shared/types/common";
import {CompareDFsResult} from "../types";
import {excelDiffService} from "../services/excelDiffService";

const useExcelDiff = () => {
    const [searchParams] = useSearchParams();
    const p_cat_num = searchParams.get('cat_num') ?? "";
    const p_dt1 = searchParams.get('dt1') ?? "";
    const p_dt2 = searchParams.get('dt2') ?? "";

    const [cat_num, setCat_num] = useState<string|number>(p_cat_num);
    const [dt1, setDt1] = useState<string|number>(p_dt1);
    const [dt2, setDt2] = useState<string|number>(p_dt2);
    const [dataLoadingStatus, setDataLoadingStatus] = useState<DataLoadingStatus>(DataLoadingStatus.NotLoaded);
    const [comparisonResult, setComparisonResult] = useState<CompareDFsResult|undefined>(undefined);
    const [checkedItems, setCheckedItems] = useState({
        unmodified: true,
        modified: true,
        new: true,
        deleted: true,
    });

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setCheckedItems({
            ...checkedItems,
            [name]: checked,
        });
    };

    const navigate = useNavigate();

    const parseDt = (dt: string | number): number | undefined => {
        if (!dt) return undefined;
        const str = dt.toString();
        if (str.startsWith('1/')) return 1 / parseInt(str.split('/')[1]);
        return parseInt(str);
    };

    const scheduleUpdateStatus = useCallback((tracking_tag: string, delay: number) => {
        setTimeout(() => {
            excelDiffService.getCompareExcelsStatus(tracking_tag).then(statusResp => {
            if (statusResp.errorCode === undefined) {
                if (statusResp.success) {
                    excelDiffService.fetchExcelsComparisonResults(tracking_tag).then(resultsResp => {
                        if (resultsResp.errorCode === undefined && resultsResp.success && resultsResp.result) {
                            setComparisonResult(resultsResp.result);
                            setDataLoadingStatus(DataLoadingStatus.Loaded);
                        } else {
                            setComparisonResult(undefined);
                            setDataLoadingStatus(DataLoadingStatus.Error);
                        }
                    });
                } else {
                    scheduleUpdateStatus(tracking_tag, Math.min(delay*1.2, 20000));
                }
            } else {
                setComparisonResult(undefined);
                setDataLoadingStatus(DataLoadingStatus.Error);
            }
        });
        }, delay);
    }, [setComparisonResult, setDataLoadingStatus]);

    const compareData = useCallback(() => {
        setDataLoadingStatus(DataLoadingStatus.Loading);
        excelDiffService.compareExcels(
            cat_num as number,
            parseDt(dt1),
            parseDt(dt2),
        ).then(resp => {
            if (resp.errorCode === undefined && resp.success && (resp.result || resp.tracking_tag)) {
                if (resp.result) {
                    setComparisonResult(resp.result);
                    setDataLoadingStatus(DataLoadingStatus.Loaded);
                } else {
                    scheduleUpdateStatus(resp.tracking_tag!, 3000)
                }
            } else {
                setComparisonResult(undefined);
                setDataLoadingStatus(DataLoadingStatus.Error);
            }
        });
    }, [setDataLoadingStatus, scheduleUpdateStatus, cat_num, dt1, dt2]);

    const filteredComparisonResult = useMemo((): CompareDFsResult|undefined => {
        return comparisonResult? {columns: comparisonResult.columns, rows: comparisonResult.rows.filter(rowRes => (
                (rowRes.status === DfEntryStatus.NotChanged && checkedItems.unmodified) ||
                (rowRes.status === DfEntryStatus.Modified && checkedItems.modified) ||
                (rowRes.status === DfEntryStatus.New && checkedItems.new) ||
                (rowRes.status === DfEntryStatus.Deprecated && checkedItems.deleted)
            ))} : undefined;
    }, [comparisonResult, checkedItems]);

    const isCompareDisabled =
        cat_num < 1 || cat_num > 9999 ||
        (dt2.toString().length !== 0 && dt2.toString().length !== 10 && dt2.toString()[0] !== '-' && !dt2.toString().startsWith('1/')) ||
        (dt1.toString().length !== 0 && dt1.toString().length !== 10 && dt1.toString()[0] !== '-' && !dt1.toString().startsWith('1/')) ||
        (dt1.toString().length !== 0 && dt2.toString().length === 0) ||
        (dt1.toString().length !== 0 && dt1.toString() < dt2.toString() && dt1.toString()[0] !== '-' && !dt1.toString().startsWith('1/')) ||
        dataLoadingStatus === DataLoadingStatus.Loading;

    const handleCompare = () => {
        navigate(`/excel-diff?cat_num=${cat_num}${dt1 ? `&dt1=${dt1}` : ''}${dt2 ? `&dt2=${dt2}` : ''}`);
        setDataLoadingStatus(DataLoadingStatus.NotLoaded);
    };

    if (dataLoadingStatus === DataLoadingStatus.NotLoaded && p_cat_num) {
        compareData();
    }

    return {
        cat_num, setCat_num,
        dt1, setDt1,
        dt2, setDt2,
        dataLoadingStatus,
        checkedItems,
        handleCheckboxChange,
        filteredComparisonResult,
        isCompareDisabled,
        handleCompare,
    };
}

export default useExcelDiff;